import {
  generateAccentPalette,
  generateDarkPalette,
  generateLightPalette,
} from '@yola/yola-palette-generator';
import { LIGHT_COLOR_TYPE, DARK_COLOR_TYPE } from '../constants/common';

// Palettes are pure functions of their input color. In live-generation mode the
// same brand colors are passed on every interaction (e.g. only `primary` changes
// while the user drags a hue slider). Caching by generator + color identity
// avoids re-deriving all 10 palettes on every call.
//
// Cache is module-level so it persists across calls within the same page session.
// Key format: "<generatorName>:<h>,<s>,<l>,<a>" — unique per color value.
const paletteCache = new Map();
const MAX_PALETTE_CACHE_SIZE = 256;

const trimPaletteCache = () => {
  while (paletteCache.size > MAX_PALETTE_CACHE_SIZE) {
    const oldestKey = paletteCache.keys().next().value;
    paletteCache.delete(oldestKey);
  }
};

const getCachedPalette = (generator, color) => {
  const key = `${generator.name}:${color.toColorString()}`;

  if (paletteCache.has(key)) {
    const cachedPalette = paletteCache.get(key);
    // Refresh insertion order to keep recently used entries.
    paletteCache.delete(key);
    paletteCache.set(key, cachedPalette);
    return cachedPalette;
  }

  const palette = generator(color);
  paletteCache.set(key, palette);
  trimPaletteCache();
  return palette;
};

const generatePalette = (color, colorType) => {
  if (colorType === LIGHT_COLOR_TYPE) {
    return getCachedPalette(generateLightPalette, color);
  }

  if (colorType === DARK_COLOR_TYPE) {
    return getCachedPalette(generateDarkPalette, color);
  }

  return getCachedPalette(generateAccentPalette, color);
};

export default generatePalette;
